import type { SchemaDefinition } from '../types/localDb.types'

/**
 * Relasi koleksi JAL WMS (docs/erd/current.md v010) yang diekspansi lewat `fields=fk.*`.
 * Alias O2M bertanda [ERD-GAP] belum tercatat di ERD dan diusulkan sebagai alias Directus.
 */
export const WMS_SCHEMA: SchemaDefinition = {
  mt_material_group: { searchFields: ['code', 'name'] },
  mt_unit: { searchFields: ['code', 'name', 'description'] },
  sla_wms_setting: { searchFields: ['code', 'name'] },
  mt_companies: { searchFields: ['code', 'name', 'address'] },
  mt_warehouses: { searchFields: ['code', 'name'] },
  /** GEN-03 Pusat Notifikasi — entitas `notification` ERD v010 (+ severity, ref_type, ref_id: usulan D53). */
  notification: {
    relations: { user_id: 'directus_users', role_id: 'mt_roles', user_created: 'directus_users' },
    searchFields: ['title', 'message']
  },
  mt_material: {
    relations: { category_id: 'mt_material_group', unit_small_id: 'mt_unit', default_purchase_unit_id: 'mt_unit' },
    aliases: { pack_standards: { collection: 'mt_material_pack_standard', field: 'material_id' } },
    searchFields: ['code', 'name']
  },
  mt_material_pack_standard: {
    relations: { material_id: 'mt_material', supplier_id: 'mt_companies', unit_big_id: 'mt_unit' }
  },
  rm_unit: {
    relations: {
      material_id: 'mt_material',
      supplier_id: 'mt_companies',
      rack_bin_id: 'mt_rack_bin',
      warehouse_zone_id: 'warehouse_zone',
      /** Pendataan Awal v7.50: baris pemetaan & sesi asal stiker (flag OPENING). */
      opening_line_id: 'rm_opening_stock',
      capture_session_id: 'initial_capture_session'
    },
    searchFields: ['qr_code', 'batch_no']
  },
  warehouse_zone: { relations: { warehouse_id: 'mt_warehouses' }, searchFields: ['code', 'name'] },
  mt_rack: { relations: { zone_id: 'warehouse_zone', category_restriction_id: 'mt_material_group' } },
  mt_rack_bin: { relations: { rack_id: 'mt_rack' }, searchFields: ['code'] },
  rm_arrival: {
    relations: { supplier_id: 'mt_companies', user_created: 'directus_users' },
    /** [ERD-GAP] alias O2M `lines` → rm_arrival_line.arrival_id. */
    aliases: { lines: { collection: 'rm_arrival_line', field: 'arrival_id' } },
    searchFields: ['arrival_no', 'sj_no']
  },
  rm_arrival_line: {
    relations: { arrival_id: 'rm_arrival', material_id: 'mt_material', unit_id: 'mt_unit' },
    /** [ERD-GAP] alias O2M `pallet_contents` → rm_pallet_content.arrival_line_id. */
    aliases: { pallet_contents: { collection: 'rm_pallet_content', field: 'arrival_line_id' } },
    searchFields: ['po_no', 'batch_no']
  },
  pallet: {
    /** `zone_id` & `sku_id` (kolom isi pallet FG, [ERD-GAP]). */
    relations: { zone_id: 'warehouse_zone', sku_id: 'mt_product' },
    /** [ERD-GAP] alias O2M `contents` & `quarantine_entries`. */
    aliases: {
      contents: { collection: 'rm_pallet_content', field: 'pallet_id' },
      quarantine_entries: { collection: 'rm_quarantine_entry', field: 'pallet_id' }
    },
    searchFields: ['code']
  },
  rm_pallet_content: { relations: { pallet_id: 'pallet', arrival_line_id: 'rm_arrival_line' } },
  rm_quarantine_entry: {
    relations: { pallet_id: 'pallet', arrival_line_id: 'rm_arrival_line' },
    /** [ERD-GAP] alias O2M `qc_assignments` → rm_qc_assignment.quarantine_entry_id. */
    aliases: { qc_assignments: { collection: 'rm_qc_assignment', field: 'quarantine_entry_id' } },
    searchFields: ['quarantine_no']
  },
  rm_qc_assignment: {
    relations: {
      quarantine_entry_id: 'rm_quarantine_entry',
      operator_id: 'directus_users',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      /** [ERD-GAP] unit Good / NG hasil assignment (kolom QR UNIT prototype IRM-04). */
      unit_id: 'rm_unit',
      ng_unit_id: 'rm_unit'
    },
    searchFields: ['assignment_no']
  },
  rm_tobacco_receipt: {
    relations: {
      supplier_id: 'mt_companies',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      user_created: 'directus_users',
      user_updated: 'directus_users'
    },
    /** [ERD-GAP] alias O2M `lines` → rm_tobacco_receipt_line.receipt_id. */
    aliases: { lines: { collection: 'rm_tobacco_receipt_line', field: 'receipt_id' } },
    searchFields: ['receipt_no']
  },
  rm_tobacco_receipt_line: { relations: { receipt_id: 'rm_tobacco_receipt', material_id: 'mt_material' } },
  ng_ticket: {
    relations: {
      unit_id: 'rm_unit',
      supplier_id: 'mt_companies',
      placement_rack_bin_id: 'mt_rack_bin',
      placement_warehouse_zone_id: 'warehouse_zone',
      quarantine_entry_id: 'rm_quarantine_entry',
      qc_assignment_id: 'rm_qc_assignment'
    },
    /** [ERD-GAP] alias O2M `ro_lines` → return_order_line.ng_ticket_id (tiket ↔ RO). */
    aliases: { ro_lines: { collection: 'return_order_line', field: 'ng_ticket_id' } },
    searchFields: ['ticket_no']
  },
  /** Return Order RM (C05). `lines`/`pickup_tasks` = [ERD-GAP] alias O2M seperti pola `lines` lain. */
  container: { relations: { zone_id: 'warehouse_zone' }, searchFields: ['code', 'name'] },
  /** Inbound FG (C08). Alias O2M `labels` / `tasks` / `items` = [ERD-GAP]. */
  fg_labeler_batch: {
    relations: {
      sku_id: 'mt_product',
      machine_id: 'mt_equipment',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      user_created: 'directus_users'
    },
    aliases: {
      labels: { collection: 'fg_label', field: 'batch_id' },
      tasks: { collection: 'fg_putaway_task', field: 'batch_id' }
    },
    searchFields: ['batch_no', 'labeler_ref']
  },
  fg_labeler_feed_log: { relations: { batch_id: 'fg_labeler_batch' } },
  fg_label: {
    relations: {
      sku_id: 'mt_product',
      batch_id: 'fg_labeler_batch',
      pallet_id: 'pallet',
      container_id: 'container',
      zone_id: 'warehouse_zone',
      /** Pengiriman terakhir label (Surat Jalan / customer) — dibaca claim v7.50. */
      shipped_plan_id: 'delivery_plan'
    },
    searchFields: ['label_no']
  },
  /** v7.50 C-09: `source_type` (PRODUCTION / CLAIM), `claim_id`, `sku_id` (tugas claim tanpa batch) = [ERD-GAP] laporan 29. */
  fg_putaway_task: {
    relations: {
      batch_id: 'fg_labeler_batch',
      sku_id: 'mt_product',
      claim_id: 'claim',
      operator_id: 'directus_users',
      user_created: 'directus_users'
    },
    aliases: { items: { collection: 'fg_putaway_task_item', field: 'task_id' } },
    searchFields: ['fpw_no']
  },
  fg_putaway_task_item: {
    relations: {
      task_id: 'fg_putaway_task',
      target_pallet_id: 'pallet',
      target_container_id: 'container',
      actual_pallet_id: 'pallet',
      actual_container_id: 'container',
      label_id: 'fg_label',
      batch_id: 'fg_labeler_batch',
      ng_pack_id: 'fg_ng_pack'
    }
  },
  /** Outbound FG (C09). Alias O2M `lines` / `tasks` / `items` / `staging` = [ERD-GAP]; kolom ekstra dicatat di laporan 13. */
  delivery_plan: {
    relations: {
      customer_id: 'mt_companies',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      user_created: 'directus_users',
      release_requested_by_id: 'directus_users',
      release_decided_by_id: 'directus_users'
    },
    aliases: {
      lines: { collection: 'delivery_plan_line', field: 'plan_id' },
      tasks: { collection: 'fg_picking_task', field: 'plan_id' },
      unpacks: { collection: 'fg_unpack_order', field: 'plan_id' },
      staging: { collection: 'fg_staging', field: 'plan_id' },
      rts_tasks: { collection: 'fg_rts_task', field: 'plan_id' }
    },
    searchFields: ['plan_no', 'sj_no']
  },
  delivery_plan_line: { relations: { plan_id: 'delivery_plan', sku_id: 'mt_product' } },
  fg_picking_task: {
    relations: { plan_id: 'delivery_plan', operator_id: 'directus_users' },
    aliases: {
      items: { collection: 'fg_picking_task_item', field: 'task_id' },
      unpacks: { collection: 'fg_unpack_order', field: 'task_id' }
    }
  },
  /** Bongkar Kemasan pesanan (BPO, prototype v7.36+; ERD-EXTENSION D45/D46). */
  fg_unpack_order: {
    relations: {
      plan_id: 'delivery_plan',
      sku_id: 'mt_product',
      operator_id: 'directus_users',
      task_id: 'fg_picking_task'
    },
    aliases: { sources: { collection: 'fg_unpack_source', field: 'unpack_id' } },
    searchFields: ['bpo_no']
  },
  fg_unpack_source: { relations: { unpack_id: 'fg_unpack_order', label_id: 'fg_label' } },
  fg_picking_task_item: {
    relations: { task_id: 'fg_picking_task', label_id: 'fg_label', from_pallet_id: 'pallet', container_id: 'container' }
  },
  fg_staging: {
    relations: { plan_id: 'delivery_plan', label_id: 'fg_label', picked_by_id: 'directus_users' }
  },
  fg_rts_task: {
    relations: { plan_id: 'delivery_plan', operator_id: 'directus_users' },
    aliases: { items: { collection: 'fg_rts_task_item', field: 'task_id' } },
    searchFields: ['rts_no']
  },
  fg_rts_task_item: {
    relations: { task_id: 'fg_rts_task', label_id: 'fg_label', to_pallet_id: 'pallet', actual_pallet_id: 'pallet' }
  },
  fg_pack_stock: {
    relations: {
      sku_id: 'mt_product',
      batch_id: 'fg_labeler_batch',
      container_id: 'container',
      /** Laporan 22: pack Good / Repack hasil claim disimpan di pallet Zone Retur Claim (RC-P1…P4), bukan container. */
      pallet_id: 'pallet',
      claim_id: 'claim'
    },
    searchFields: ['label_no']
  },
  /** Merge & Konsolidasi FG (C10). `items` = [ERD-GAP] alias O2M; `merge_request_item` / `consolidation_task_item` = [ERD-GAP]. */
  merge_request: {
    relations: {
      sku_id: 'mt_product',
      operator_id: 'directus_users',
      approved_by_id: 'directus_users',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      linked_request_id: 'merge_request',
      user_created: 'directus_users'
    },
    aliases: { items: { collection: 'merge_request_item', field: 'request_id' } },
    searchFields: ['png_no']
  },
  merge_request_item: {
    relations: {
      request_id: 'merge_request',
      label_id: 'fg_label',
      pack_stock_id: 'fg_pack_stock',
      sku_id: 'mt_product',
      batch_id: 'fg_labeler_batch',
      from_pallet_id: 'pallet',
      from_container_id: 'container'
    }
  },
  consolidation_task: {
    relations: {
      sku_id: 'mt_product',
      from_pallet_id: 'pallet',
      to_pallet_id: 'pallet',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      operator_id: 'directus_users',
      user_created: 'directus_users'
    },
    aliases: { items: { collection: 'consolidation_task_item', field: 'task_id' } },
    searchFields: ['ksl_no']
  },
  consolidation_task_item: {
    relations: {
      task_id: 'consolidation_task',
      label_id: 'fg_label',
      from_pallet_id: 'pallet',
      to_pallet_id: 'pallet',
      final_pallet_id: 'pallet'
    }
  },
  /**
   * Customer Claim FG (C11, v7.50). Alias O2M `items` / `sort_tasks` / `store_tasks` / `putaway_tasks` = [ERD-GAP].
   * Rencana kedatangan dan penerimaan (`claim_arrival_plan*`, `claim_receipt*`) dihapus bersama alurnya (laporan 29).
   */
  claim: {
    relations: {
      customer_id: 'mt_companies',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      d2_decided_by_id: 'directus_users',
      processed_by_id: 'directus_users',
      user_created: 'directus_users'
    },
    aliases: {
      items: { collection: 'claim_item', field: 'claim_id' },
      sort_tasks: { collection: 'claim_sort_task', field: 'claim_id' },
      store_tasks: { collection: 'claim_store_task', field: 'claim_id' },
      putaway_tasks: { collection: 'fg_putaway_task', field: 'claim_id' }
    },
    searchFields: ['clm_no', 'sj_list', 'lot_no']
  },
  claim_item: {
    relations: {
      claim_id: 'claim',
      label_id: 'fg_label',
      customer_id: 'mt_companies',
      reject_reason_id: 'mt_reject_type',
      repack_reason_id: 'mt_reject_type',
      sort_task_id: 'claim_sort_task',
      scanned_by_id: 'directus_users',
      sorted_by_id: 'directus_users'
    }
  },
  claim_sort_task: {
    relations: { claim_id: 'claim', operator_id: 'directus_users' },
    aliases: { items: { collection: 'claim_item', field: 'sort_task_id' } },
    searchFields: ['bkr_no']
  },
  claim_store_task: {
    relations: {
      claim_id: 'claim',
      target_pallet_id: 'pallet',
      from_pallet_id: 'pallet',
      operator_id: 'directus_users'
    },
    searchFields: ['spc_no']
  },
  fg_ng_pack: {
    relations: {
      claim_id: 'claim',
      claim_item_id: 'claim_item',
      pallet_id: 'pallet',
      reason_id: 'mt_reject_type',
      destruction_id: 'fg_destruction'
    }
  },
  /** Pemusnahan FG (C12). `tasks` = [ERD-GAP] alias O2M → fg_destruction_task.destruction_id (tugas pindah ke ZM-01). */
  fg_destruction: {
    relations: {
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      approver_id: 'directus_users',
      scheduled_by_id: 'directus_users'
    },
    aliases: { tasks: { collection: 'fg_destruction_task', field: 'destruction_id' } },
    searchFields: ['dsp_no']
  },
  fg_destruction_task: {
    relations: { destruction_id: 'fg_destruction', operator_id: 'directus_users' },
    aliases: { lines: { collection: 'fg_destruction_task_line', field: 'task_id' } },
    searchFields: ['task_no']
  },
  fg_destruction_task_line: { relations: { task_id: 'fg_destruction_task', pack_id: 'fg_ng_pack' } },
  return_order: {
    relations: {
      supplier_id: 'mt_companies',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      preparer_id: 'directus_users',
      user_created: 'directus_users'
    },
    aliases: {
      lines: { collection: 'return_order_line', field: 'ro_id' },
      pickup_tasks: { collection: 'rm_ng_pickup_task', field: 'ro_id' }
    },
    searchFields: ['ro_no']
  },
  return_order_line: { relations: { ro_id: 'return_order', ng_ticket_id: 'ng_ticket' } },
  rm_ng_pickup_task: {
    relations: { ro_id: 'return_order', operator_id: 'directus_users', user_created: 'directus_users' },
    searchFields: ['task_no']
  },
  mt_reject_type: { searchFields: ['code', 'name'] },
  stock_movement: {
    relations: {
      unit_id: 'rm_unit',
      from_rack_bin_id: 'mt_rack_bin',
      to_rack_bin_id: 'mt_rack_bin',
      from_warehouse_zone_id: 'warehouse_zone',
      to_warehouse_zone_id: 'warehouse_zone',
      user_created: 'directus_users'
    }
  },
  tx_log: {
    relations: { user_id: 'directus_users', warehouse_id: 'mt_warehouses' },
    searchFields: ['doc_ref', 'detail']
  },
  rm_putaway_task: {
    relations: { operator_id: 'directus_users', user_created: 'directus_users' },
    aliases: { items: { collection: 'rm_putaway_task_item', field: 'task_id' } },
    searchFields: ['task_no']
  },
  rm_putaway_task_item: {
    relations: {
      task_id: 'rm_putaway_task',
      unit_id: 'rm_unit',
      recommended_rack_bin_id: 'mt_rack_bin',
      recommended_warehouse_zone_id: 'warehouse_zone',
      actual_rack_bin_id: 'mt_rack_bin',
      actual_warehouse_zone_id: 'warehouse_zone',
      user_updated: 'directus_users'
    }
  },
  purchase_request: {
    relations: {
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      user_created: 'directus_users',
      user_updated: 'directus_users'
    },
    /** [ERD-GAP] alias O2M `lines` → purchase_request_line.pr_id. */
    aliases: { lines: { collection: 'purchase_request_line', field: 'pr_id' } },
    searchFields: ['pr_no']
  },
  purchase_request_line: {
    relations: {
      pr_id: 'purchase_request',
      material_id: 'mt_material',
      unit_id: 'mt_unit',
      supplier_id: 'mt_companies'
    }
  },
  /** Request Production C06 (WF-REQ-01). `lines`/`handovers` = [ERD-GAP] alias O2M seperti pola `lines` lain. */
  material_request: {
    relations: {
      sku_id: 'mt_product',
      staging_zone_id: 'warehouse_zone',
      prod_unit_id: 'mt_unit',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      user_created: 'directus_users'
    },
    aliases: {
      lines: { collection: 'material_request_line', field: 'request_id' },
      handovers: { collection: 'handover', field: 'request_id' }
    },
    searchFields: ['req_no', 'spk_no']
  },
  material_request_line: {
    relations: {
      request_id: 'material_request',
      material_id: 'mt_material',
      machine_id: 'mt_equipment',
      unit_id: 'mt_unit'
    },
    /** [ERD-GAP] alias O2M `pick_items` → picking_task_item.line_id (diambil/diserahkan per baris). */
    aliases: { pick_items: { collection: 'picking_task_item', field: 'line_id' } }
  },
  mt_product: {
    aliases: { bom: { collection: 'mt_bill_of_material', field: 'sku_id' } },
    searchFields: ['code', 'name', 'sku_code']
  },
  mt_bill_of_material: { relations: { sku_id: 'mt_product', material_id: 'mt_material', unit_id: 'mt_unit' } },
  /** [ERD-GAP] `machine_id` pada tugas = mesin tujuan permintaan tembakau (satu tugas per mesin). */
  picking_task: {
    relations: { operator_id: 'directus_users', machine_id: 'mt_equipment', user_created: 'directus_users' },
    aliases: { items: { collection: 'picking_task_item', field: 'task_id' } },
    searchFields: ['pick_no', 'doc_ref']
  },
  /** [ERD-GAP] `line_id`, `handover_id`, `qty_plan`, `is_wip`, `picked_at` belum ada di ERD. */
  picking_task_item: {
    relations: {
      task_id: 'picking_task',
      unit_id: 'rm_unit',
      line_id: 'material_request_line',
      from_rack_bin_id: 'mt_rack_bin',
      from_warehouse_zone_id: 'warehouse_zone',
      handover_id: 'handover',
      transfer_line_id: 'transfer_line'
    }
  },
  handover: {
    relations: { request_id: 'material_request', machine_id: 'mt_equipment', received_by: 'directus_users' },
    aliases: { items: { collection: 'picking_task_item', field: 'handover_id' } },
    searchFields: ['handover_no', 'area_code']
  },
  /** Transfer antar gudang C07 (WF-TRF-01). `lines` = [ERD-GAP] alias O2M → transfer_line.transfer_id; `pick_tasks` tidak dialias (dicari via `doc_ref`). */
  transfer: {
    relations: {
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      shortage_pr_id: 'purchase_request',
      user_created: 'directus_users'
    },
    aliases: { lines: { collection: 'transfer_line', field: 'transfer_id' } },
    searchFields: ['itr_no', 'sji_no']
  },
  transfer_line: {
    relations: { transfer_id: 'transfer', material_id: 'mt_material', unit_id: 'mt_unit' }
  },
  /** Retur sisa WIP C07 (WF-WIP-01). */
  wip_return: {
    relations: {
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      putaway_operator_id: 'directus_users',
      user_created: 'directus_users'
    },
    aliases: { lines: { collection: 'wip_return_line', field: 'wip_return_id' } },
    searchFields: ['rwip_no', 'spk_no']
  },
  wip_return_line: {
    relations: {
      wip_return_id: 'wip_return',
      material_id: 'mt_material',
      unit_id: 'mt_unit',
      good_unit_id: 'rm_unit',
      ng_unit_id: 'rm_unit'
    }
  },
  /** Retur tembakau C07 (WF-TOB-02). */
  rm_tobacco_return: {
    relations: {
      machine_id: 'mt_equipment',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      user_created: 'directus_users'
    },
    aliases: { lines: { collection: 'rm_tobacco_return_line', field: 'return_id' } },
    searchFields: ['return_no']
  },
  rm_tobacco_return_line: {
    relations: {
      return_id: 'rm_tobacco_return',
      machine_id: 'mt_equipment',
      material_id: 'mt_material',
      unit_id: 'rm_unit'
    }
  },
  /**
   * Pendataan Awal v7.50 (WF tanpa Workflow Studio, laporan 30 / ERD bagian 19). `lines` / `areas` / `scans` = alias O2M.
   * Status: RUNNING → AWAITING_APPROVAL → COMPLETED (atau CANCELLED saat berkas Excel diganti).
   */
  initial_capture_session: {
    relations: {
      user_created: 'directus_users',
      opname_started_by: 'directus_users',
      closed_by: 'directus_users',
      approved_by: 'directus_users',
      returned_by: 'directus_users'
    },
    aliases: {
      lines: { collection: 'rm_opening_stock', field: 'session_id' },
      areas: { collection: 'initial_capture_area', field: 'session_id' },
      scans: { collection: 'initial_capture_scan', field: 'session_id' }
    },
    searchFields: ['pdw_no']
  },
  /** Satu baris per rack per sesi: operator yang mendapat rack + waktu kirim hasil (null = belum / dibuka lagi). */
  initial_capture_area: {
    relations: {
      session_id: 'initial_capture_session',
      rack_id: 'mt_rack',
      operator_id: 'directus_users'
    }
  },
  /** Hasil scan opname pertama: PRESENT (stiker di posisi), EMPTY (posisi kosong), LOST (dinyatakan hilang Admin). */
  initial_capture_scan: {
    relations: {
      session_id: 'initial_capture_session',
      unit_id: 'rm_unit',
      rack_bin_id: 'mt_rack_bin',
      by_id: 'directus_users'
    }
  },
  /** ERD `rm_opening_stock` dipakai ulang sebagai baris pemetaan Pendataan Awal (satu material Excel per baris). */
  rm_opening_stock: {
    relations: {
      session_id: 'initial_capture_session',
      material_id: 'mt_material',
      unit_id: 'mt_unit',
      small_unit_id: 'mt_unit'
    },
    searchFields: ['source_name']
  },
  rm_move_task: {
    relations: { operator_id: 'directus_users', user_created: 'directus_users', by_id: 'directus_users' },
    /** [ERD-GAP] alias O2M `items` → rm_move_task_item.task_id. */
    aliases: { items: { collection: 'rm_move_task_item', field: 'task_id' } },
    searchFields: ['pdh_no']
  },
  /** [ERD-GAP] baris tugas pindah (ERD hanya menulis "items" pada `rm_move_task`). */
  rm_move_task_item: {
    relations: {
      task_id: 'rm_move_task',
      material_id: 'mt_material',
      unit_id: 'rm_unit',
      from_rack_bin_id: 'mt_rack_bin',
      suggested_rack_bin_id: 'mt_rack_bin',
      final_rack_bin_id: 'mt_rack_bin'
    }
  },
  rm_ng_item: {
    relations: {
      unit_id: 'rm_unit',
      material_id: 'mt_material',
      reason_id: 'mt_reject_type',
      rack_bin_id: 'mt_rack_bin',
      warehouse_zone_id: 'warehouse_zone',
      move_operator_id: 'directus_users',
      destruction_id: 'rm_destruction',
      destroy_operator_id: 'directus_users'
    },
    searchFields: ['ngz_no', 'sticker_ngq']
  },
  /** Pemusnahan RM (C12) — BAP-RM; kolom tambahan = [ERD-GAP] (laporan 18). */
  rm_destruction: {
    relations: {
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      approver_id: 'directus_users',
      done_by_id: 'directus_users'
    },
    searchFields: ['bap_no']
  },
  /** Stock Opname (C13) — kolom tambahan = [ERD-GAP] (laporan 20). */
  opname_schedule: { relations: { warehouse_id: 'mt_warehouses' } },
  opname_session: {
    relations: {
      warehouse_id: 'mt_warehouses',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      closed_by_id: 'directus_users',
      validated_by_id: 'directus_users'
    },
    searchFields: ['opn_no']
  },
  opname_task: {
    relations: { session_id: 'opname_session', operator_id: 'directus_users', validated_by_id: 'directus_users' },
    searchFields: ['opt_no']
  },
  opname_scan: {
    relations: {
      task_id: 'opname_task',
      session_id: 'opname_session',
      rack_bin_id: 'mt_rack_bin',
      warehouse_zone_id: 'warehouse_zone',
      material_id: 'mt_material',
      sku_id: 'mt_product',
      unit_id: 'rm_unit',
      label_id: 'fg_label',
      pack_stock_id: 'fg_pack_stock',
      ng_reason_id: 'mt_reject_type',
      discrepancy_id: 'discrepancy'
    }
  },
  discrepancy: {
    relations: {
      unit_id: 'rm_unit',
      label_id: 'fg_label',
      transfer_id: 'transfer',
      rack_bin_id: 'mt_rack_bin',
      warehouse_zone_id: 'warehouse_zone',
      status_id: 'wml_status',
      submission_id: 'wxl_submission',
      approver_id: 'directus_users',
      session_id: 'opname_session',
      task_id: 'opname_task',
      warehouse_id: 'mt_warehouses',
      material_id: 'mt_material',
      sku_id: 'mt_product',
      pack_stock_id: 'fg_pack_stock',
      recheck_operator_id: 'directus_users',
      resolved_by_id: 'directus_users'
    },
    searchFields: ['dsc_no', 'scanned_code']
  }
}
